import type React from 'react';
import type { Process } from '../type/Process';

type SetProcess = React.Dispatch<React.SetStateAction<Process>>;
type ProcessUpdater = React.SetStateAction<Process>;

function applyAutoToggle(
  prev: Process,
  next: Process,
  keyPairs: Array<[keyof Process, string, boolean]>
): Process {
  let result = next;
  for (const [processKey, mandatoryKey, isBrand] of keyPairs) {
    if (next[processKey] !== prev[processKey]) {
      const val = next[processKey];
      const isEmpty = val === null || val === '' || val === undefined;
      const has = result.mandatoryFields.includes(mandatoryKey);
      if (isEmpty && !has && !isBrand) {
        result = { ...result, mandatoryFields: [...result.mandatoryFields, mandatoryKey] };
      } else if (!isEmpty && has) {
        result = { ...result, mandatoryFields: result.mandatoryFields.filter((f) => f !== mandatoryKey) };
      }
    }
  }
  return result;
}

function useSectionSetProcess(
  setFormProcess: SetProcess,
  keyPairs: Array<[keyof Process, string, boolean]>
): SetProcess {
  return (updater: ProcessUpdater) => {
    setFormProcess((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return applyAutoToggle(prev, next, keyPairs);
    });
  };
}

const CUTTING_KEYS: Array<[keyof Process, string, boolean]> = [
  ['ae', 'ae', false],
  ['ap', 'ap', false],
  ['vc', 'vc', false],
  ['fz', 'fz', false],
];

const MACHINED_KEYS: Array<[keyof Process, string, boolean]> = [
  ['machinedPieces', 'pieces', false],
  ['malround', 'malrond', false],
];

const OPERATOR_KEYS: Array<[keyof Process, string, boolean]> = [
  ['operation', 'operation', false],
];

const TOOLING_KEYS: Array<[keyof Process, string, boolean]> = [
  ['lubType', 'lubType', false],
  ['lubBrand', 'lubBrand', true],
  ['holderType', 'holderType', false],
  ['holderBrand', 'holderBrand', true],
  ['clampingType', 'clampingType', false],
  ['clampingBrand', 'clampingBrand', true],
];

export function useCuttingSetProcess(setFormProcess: SetProcess): SetProcess {
  return useSectionSetProcess(setFormProcess, CUTTING_KEYS);
}

export function useMachinedSetProcess(setFormProcess: SetProcess): SetProcess {
  return useSectionSetProcess(setFormProcess, MACHINED_KEYS);
}

export function useOperatorSetProcess(setFormProcess: SetProcess): SetProcess {
  return useSectionSetProcess(setFormProcess, OPERATOR_KEYS);
}

export function useToolingSetProcess(setFormProcess: SetProcess): SetProcess {
  return useSectionSetProcess(setFormProcess, TOOLING_KEYS);
}
